import { HeartHandshake, Users } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useBuySponsorships, useDonate } from "@/features/payments/hooks/usePayments";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";

/**
 * Giving money that is not your own membership: paying for somebody else's,
 * and a standing contribution.
 *
 * Shared by the billing page and the registration form's last step. Neither
 * needs anybody's approval, which is why they can be offered to an applicant
 * whose own membership is still with an administrator: sponsoring somebody and
 * donating are gifts, and a gift does not wait in a queue.
 *
 * Both open Stripe, so anything unsaved on the page around them is lost. The
 * form says so where it offers them.
 */
export function SponsorButton() {
  const buySponsorships = useBuySponsorships();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        variant="outline"
        disabled={buySponsorships.isPending}
        onClick={() =>
          buySponsorships.mutate(
            { quantity: 1 },
            { onError: (e) => toast.error(e.message) }
          )
        }
      >
        {buySponsorships.isPending && <Spinner data-slot="icon" />}
        <Users data-slot="icon" /> Sponsor a Member
      </Button>
      <span className="text-muted-foreground text-xs">
        Pay for someone else&apos;s membership. You name who it&apos;s for
        whenever you like.
      </span>
    </div>
  );
}

/** Presets plus a box, because a contribution is not a price list. */
const DONATION_PRESETS = [25, 50, 100, 250];

export function DonationFields() {
  const donate = useDonate();
  const [chosen, setChosen] = useState<number | "other">(50);
  const [other, setOther] = useState("");

  const dollars = chosen === "other" ? Number(other) : chosen;
  const valid = Number.isFinite(dollars) && dollars >= 5 && dollars <= 10000;

  // `dollars` shadows the currency helper inside this component only; the
  // amount here is the thing being named.
  return (
    <div className="space-y-2 border-t pt-4">
      <p className="text-sm font-medium">
        <HeartHandshake className="mr-1.5 inline size-4" />
        Donate annually
      </p>
      <p className="text-muted-foreground text-xs">
        A recurring, tax-deductible contribution towards AFELL&apos;s
        administration costs, charged once a year. Stop or change it any time
        from the list above.
      </p>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {DONATION_PRESETS.map((amount) => (
            <button
              key={amount}
              type="button"
              aria-pressed={chosen === amount}
              onClick={() => setChosen(amount)}
              className={
                chosen === amount
                  ? "bg-primary text-primary-foreground rounded-md px-3 py-1.5 text-sm"
                  : "hover:bg-accent rounded-md border px-3 py-1.5 text-sm"
              }
            >
              ${amount}
            </button>
          ))}
          <button
            type="button"
            aria-pressed={chosen === "other"}
            onClick={() => setChosen("other")}
            className={
              chosen === "other"
                ? "bg-primary text-primary-foreground rounded-md px-3 py-1.5 text-sm"
                : "hover:bg-accent rounded-md border px-3 py-1.5 text-sm"
            }
          >
            Other
          </button>
          {chosen === "other" && (
            <span className="flex items-center gap-1">
              <span className="text-muted-foreground text-sm">$</span>
              <Input
                aria-label="Yearly amount in dollars"
                inputMode="decimal"
                className="h-9 w-24"
                value={other}
                onChange={(e) => setOther(e.target.value)}
              />
            </span>
          )}
          <span className="text-muted-foreground text-sm">a year</span>
        </div>
        <Button
          variant="outline"
          disabled={!valid || donate.isPending}
          onClick={() =>
            donate.mutate(Math.round(dollars * 100), {
              onError: (e) => toast.error(e.message),
            })
          }
        >
          {donate.isPending && <Spinner data-slot="icon" />}
          {valid ? `Donate $${dollars}/year` : "Donate"}
        </Button>
      </div>
    </div>
  );
}
